import React from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Inbox, Users, Calendar, LogOut,
  Wallet, Building2, AlertTriangle, CreditCard, MessageSquare,
  FileSpreadsheet, ShieldCheck, Handshake,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BrandLogo } from "../components/BrandLogo";
import { useNotifications, NotificationsProvider } from "../hooks/useNotifications";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, key: null },
  { to: "/admin/mensajes", label: "Mensajes web", icon: MessageSquare, key: "mensajes_nuevos" },
  { to: "/admin/formularios-web", label: "Central de Leads", icon: FileSpreadsheet, key: "formularios_nuevos" },
  { to: "/admin/spm", label: "Registro SPM", icon: ShieldCheck, key: "spm_nuevos" },
  { to: "/admin/afiliados", label: "Central Afiliados", icon: Handshake, key: null },
  { to: "/admin/cotizaciones", label: "Cotizaciones", icon: Inbox, key: null },
  { to: "/admin/cartas", label: "Cartas asignación", icon: FileText, key: null },
  { to: "/admin/comunidades", label: "Comunidades", icon: Building2, key: null },
  { to: "/admin/clientes", label: "Clientes & Pólizas", icon: Users, key: null },
  { to: "/admin/cobranza", label: "Cobranza", icon: CreditCard, key: null },
  { to: "/admin/siniestros", label: "Siniestros", icon: AlertTriangle, key: null },
  { to: "/admin/renovaciones", label: "Renovaciones", icon: Calendar, key: null },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>;
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <NotificationsProvider>
      <AdminShell user={user} logout={logout} nav={nav} />
    </NotificationsProvider>
  );
}

function AdminShell({ user, logout, nav }) {
  const { counts } = useNotifications();

  // Update document title with total pending count
  if (typeof document !== "undefined") {
    const base = "MiComunidad Admin";
    document.title = counts.total_pendientes > 0 ? `(${counts.total_pendientes}) ${base}` : base;
  }

  return (
    <div className="admin-shell">
      <aside className="flex flex-col p-5 text-white" style={{ background: "var(--brand-navy)" }} data-testid="admin-sidebar">
        <Link to="/" className="block mb-10">
          <BrandLogo size={36} variant="light" subline="Panel admin" iconBg="rgba(255,255,255,0.04)"/>
        </Link>

        <nav className="space-y-1 flex-1">
          {NAV.map((n) => {
            const c = n.key ? counts[n.key] || 0 : 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({isActive}) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s|&/g,"-")}`}
              >
                <n.icon size={16} />
                <span className="flex-1">{n.label}</span>
                {c > 0 && (
                  <span
                    className="inline-flex items-center justify-center text-[10.5px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] leading-none"
                    style={{ background: "var(--brand-accent)", color: "white" }}
                    data-testid={`badge-${n.key}`}
                  >
                    {c > 99 ? "99+" : c}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="pt-5 mt-5 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="text-[11px] opacity-70 mb-1">{user.name || "Admin"}</div>
          <div className="text-[10px] opacity-50 mb-3 truncate">{user.email}</div>
          <button className="sidebar-link w-full text-left" onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout">
            <LogOut size={15}/> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="p-6 md:p-10 overflow-auto" style={{ background: "var(--bg-soft)" }}>
        <Outlet />
      </div>
    </div>
  );
}
